import React from 'react';

export const StrategyMapTimelineViewSkeleton: React.FC = () => {
  // Generate realistic skeleton data that matches the actual gantt timeline
  const skeletonTasks = [
    {
      name: "Speed Up Sign Up - Backend enhancements",
      stage: "EXECUTING",
      status: "Delayed",
      startCol: 4,
      width: 14,
      color: "border-gray-200 bg-white dark:bg-gray-800 dark:border-gray-600",
    },
    {
      name: "Speed Up Sign Up - Replace Okta Login",
      stage: "EXECUTING",
      status: "Unallocated",
      startCol: 15,
      width: 8,
      color: "border-gray-200 bg-white dark:bg-gray-800 dark:border-gray-600",
    },
    {
      name: "API Performance Optimization",
      stage: "PLANNING",
      status: "On Track",
      startCol: 2,
      width: 6,
      color: "border-gray-200 bg-white dark:bg-gray-800 dark:border-gray-600",
    },
    {
      name: "Database Migration Tasks",
      stage: "EXECUTING",
      status: "Critical",
      startCol: 10,
      width: 11,
      color: "border-gray-200 bg-white dark:bg-gray-800 dark:border-gray-600",
    },
    {
      name: "Frontend UI Improvements",
      stage: "TESTING",
      status: "Completed",
      startCol: 5,
      width: 7,
      color: "border-gray-200 bg-white dark:bg-gray-800 dark:border-gray-600",
    },
  ];

  // Generate month header and days like in the real timeline
  const monthName = "April 2024";
  const days = [
    1, 2, 3, 4, 5, 8, 9, 10, 11, 12, 15, 16, 17, 18, 19, 22, 23, 24, 25, 26, 29,
    30, 1, 2,
  ];
  const totalColumns = days.length;

  return (
    <div className="relative w-full h-full min-h-[400px] bg-white dark:bg-[#111928]">
      {/* Header Section - exactly matching the real timeline */}
      <div className="sticky top-0 z-10 bg-gray-50 dark:bg-[#111928]">
        {/* Month Header */}
        <div className="bg-white dark:bg-[#1a1f2e] border-b border-gray-300 dark:border-gray-700 py-2 text-center">
          <div className="animate-pulse">
            <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded mx-auto w-32"></div>
          </div>
        </div>

        {/* Days Header */}
        <div className="flex bg-white dark:bg-[#1a1f2e] border-b border-gray-300 dark:border-gray-700">
          {days.map((day, index) => (
            <div
              key={`day-${index}`}
              className="flex-1 min-w-[50px] px-2 py-2 text-center border-r border-gray-300 dark:border-gray-600"
              style={{ minWidth: `${100 / totalColumns}%` }}
            >
              <div className="animate-pulse">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-6 mx-auto"></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Grid Lines - matching the real timeline grid */}
      <div className="absolute  left-0 w-full h-full">
        {days.map((_, index) => (
          <div
            key={`grid-line-${index}`}
            className="absolute top-0 h-full border-r border-gray-500 dark:border-[#3B465B]"
            style={{
              left: `${(index / totalColumns) * 100}%`,
              width: "1px",
              opacity: index % 5 === 0 ? 0.4 : 0.15,
            }}
          />
        ))}
      </div>

      {/* Skeleton Task Rows - matching real task bar styling */}
      <div className="pt-[88px] animate-pulse">
        {skeletonTasks.map((task, rowIndex) => (
          <div key={`task-${rowIndex}`} className="relative h-[90px]">
            {/* Task Bar - positioned like real gantt bars */}
            <div
              className={`absolute top-6 h-[60px] rounded border-l-4 border-t-2  border-r-2 border-b-2  ${task.color} shadow-sm`}
              style={{
                left: `${(task.startCol / totalColumns) * 100}%`,
                width: `${(task.width / totalColumns) * 100}%`,
                minWidth: "120px",
              }}
            >
              {/* Task Content - matching real task bar layout */}
              <div className="p-2.5 h-full flex flex-col justify-between">
                {/* Stage and Status */}
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <div className="h-2 bg-gray-300 dark:bg-gray-600 rounded w-16"></div>
                    <div className="h-2 bg-gray-300 dark:bg-gray-600 rounded w-12"></div>
                  </div>

                  {/* Task Title */}
                  <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-full max-w-[250px]"></div>

                  {/* Status and Date Range */}
                  <div className="h-2 bg-gray-300 dark:bg-gray-600 rounded w-24"></div>
                </div>

                {/* Progress Bar at bottom of task */}
                <div className="mt-2.5">
                  <div className="h-1 bg-gray-200 -ml-3 bottom-0 dark:bg-gray-700 rounded-full">
                    <div
                      className="h-1 bg-green-400 dark:bg-green-500 rounded-full"
                      style={{ width: `${[60, 80, 30, 45, 70][rowIndex]}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Loading indicator */}
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-10">
        <div className="flex items-center space-x-2 text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 px-3 py-2 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="w-4 h-4 border-2 border-blue-500 dark:border-blue-400 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-sm font-medium">Loading timeline...</span>
        </div>
      </div>
    </div>
  );
};

